import "./App.css";
import PersonaButton from "./Components/PersonaButton";
import PreloadedQuestions from "./Components/PreloadedQuestions";
import ChatDialog from "./Components/ChatDialog";
import { getConversationId } from "./conversation";
import { getNonce, refreshNonce, isNonceFailure } from "./session";
import {
  chatError,
  describeError,
  visitorMessage,
  errorCodeLabel,
  isRetryable,
  reportChatError,
} from "./errors";
import React, { useState, useEffect } from "react";

const DEFAULT_REST_URL = "/wp-json/cmw/v1/chat";
const WELCOME_KEY = "seenChatWelcome";

// Record the moment the welcome teaser was dismissed/seen (timestamp, ms).
function markWelcomeSeen() {
  try {
    localStorage.setItem(WELCOME_KEY, String(Date.now()));
  } catch (e) {
    /* storage unavailable */
  }
}

// Show the teaser if it has never been dismissed, or if the reset window has
// elapsed since it was. resetMs <= 0 means always show. Legacy "1" flags (no
// timestamp) are treated as expired so they show once more, then start timing.
function shouldShowWelcome(resetMs) {
  try {
    const raw = localStorage.getItem(WELCOME_KEY);
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    if (!ts || Number.isNaN(ts)) return true;
    if (resetMs <= 0) return true;
    return Date.now() - ts >= resetMs;
  } catch (e) {
    return true;
  }
}

// The server caps its own OpenAI call at 30s, so give a real answer headroom
// past that. The follow-up suggestion call is a 64-token throwaway and must
// never hold the widget open.
const REQUEST_TIMEOUT_MS = 45000;
const INTERNAL_TIMEOUT_MS = 20000;

// Returns { answer, citations, links, disclaimer }. Tolerates the legacy {response} shape.
// Throws a chatError() carrying the code, status and the server's own message
// separately, so the caller decides what a visitor is allowed to read.
//
// A rejected nonce is retried once with a freshly fetched one: the page may
// have come from a cache older than the nonce's lifetime, which is invisible
// to us until the server says so.
async function sendPromptToApi(message, restConfig = {}, internal = false) {
  try {
    return await postPrompt(message, restConfig, internal);
  } catch (error) {
    if (!isNonceFailure(error)) throw error;

    const fresh = await refreshNonce(restConfig);
    if (!fresh) throw error;

    return postPrompt(message, restConfig, internal);
  }
}

async function postPrompt(message, restConfig = {}, internal = false) {
  const restUrl = restConfig.url || DEFAULT_REST_URL;
  const restNonce = await getNonce(restConfig);

  // "No connection" and "couldn't reach the server" need different copy and
  // different reporting, so separate them before we even try.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    throw chatError("network", "client_offline", 0, "", "navigator.onLine=false");
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const limitMs = internal ? INTERNAL_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
  const timer = controller ? setTimeout(() => controller.abort(), limitMs) : null;

  let response;
  try {
    response = await fetch(restUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": restNonce,
      },
      // `internal` requests (e.g. generating follow-up suggestions) are excluded
      // from the server-side chat history log.
      body: JSON.stringify({ message, conversation_id: getConversationId(), internal }),
      signal: controller ? controller.signal : undefined,
    });
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw chatError("timeout", "client_timeout", 0, "", "aborted after " + limitMs + "ms");
    }
    throw chatError("network", "client_network", 0, "", (e && e.message) || "fetch failed");
  } finally {
    if (timer) clearTimeout(timer);
  }

  // Read as text and parse it ourselves. A proxy 502 or a WAF block page is
  // HTML, and response.json() would throw before response.ok is ever consulted
  // — making an infrastructure failure look like a parse bug.
  let raw = "";
  try {
    raw = await response.text();
  } catch (e) {
    raw = "";
  }

  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (e) {
    data = null;
  }

  if (!response.ok) {
    if (!data || typeof data !== "object") {
      // Not our error shape at all: something in front of WordPress answered.
      throw chatError(
        "http_nonjson",
        "client_http_" + response.status,
        response.status,
        "",
        raw.slice(0, 160)
      );
    }
    const cmw = (data.data && data.data.cmw) || null;
    const code =
      typeof data.code === "string" && data.code ? data.code : "client_http_" + response.status;
    // WP puts the intended status in data.data.status; a proxy can rewrite the
    // transport one, so prefer the server's.
    const status = (data.data && Number(data.data.status)) || response.status;
    const serverMessage =
      (typeof data.message === "string" && data.message) ||
      (typeof data.error === "string" && data.error) ||
      "";
    const err = chatError("http", code, status, serverMessage);
    if (cmw) {
      // The normalizer's own verdict wins over our local guess.
      err.ref = typeof cmw.ref === "string" ? cmw.ref : "";
      if (typeof cmw.retryable === "boolean") err.retryable = cmw.retryable;
      if (cmw.debug) err.detail = JSON.stringify(cmw.debug).slice(0, 200);
    }
    throw err;
  }

  if (!data || typeof data !== "object") {
    // A 200 with a body we can't read — a plugin printing a notice, or a
    // truncated response. Distinct from a real HTTP error.
    throw chatError("parse", "client_bad_json", response.status, "", raw.slice(0, 160));
  }

  return {
    answer: typeof data.answer === "string" ? data.answer : data.response || "",
    citations: Array.isArray(data.citations) ? data.citations : [],
    // Items the server named in the prompt: used to link mentions the model
    // left as plain text, so every item referred to ends up linked.
    links: Array.isArray(data.links) ? data.links : [],
    // Present only when the REST request came from a chatbot manager.
    debug: data.debug && typeof data.debug === "object" ? data.debug : null,
    disclaimer: typeof data.disclaimer === "string" ? data.disclaimer : "",
  };
}

// Suggestions come back as free model text. Keep at most two short, non-empty
// lines; anything junk falls back to the prompts already on screen, so the
// question panel can never be left showing its spinner forever.
function parseSuggestions(answer) {
  if (typeof answer !== "string") return [];
  return answer
    .split("|||")
    .map((s) => s.replace(/^["'\s\-–—]+|["'\s]+$/g, ""))
    .filter((s) => s.length > 0 && s.length <= 80)
    .slice(0, 2);
}

// Admin-only retrieval trace. The server omits `debug` for visitors, so this
// prints nothing for them. Answers the question the Sources list can't: what
// did retrieval actually return, and where did a given document rank?
function logDebug(question, resp) {
  const debug = resp && resp.debug;
  if (!debug || typeof console === "undefined") return;

  const retrieval = Array.isArray(debug.retrieval) ? debug.retrieval : [];
  const cited = Array.isArray(debug.cited) ? debug.cited : [];
  const label =
    "Chatbot · " +
    (debug.cached ? "cached · " : "") +
    (debug.retrieved === null || typeof debug.retrieved === "undefined"
      ? "retrieved n/a"
      : debug.retrieved + " retrieved") +
    " · " +
    cited.length +
    " cited" +
    (debug.declined ? " · DECLINED (" + debug.declined + ")" : "");

  const group = console.groupCollapsed || console.group || console.log;
  group.call(console, label);
  console.log("question:", question);
  if (debug.model) console.log("model:", debug.model);

  if (retrieval.length && console.table) {
    console.log("retrieved chunks, best match first:");
    console.table(retrieval, ["rank", "score", "pdf", "title", "filename"]);
  } else if (!retrieval.length) {
    console.log(
      debug.cached
        ? "retrieval: not recorded (answer served from cache)"
        : "retrieval: nothing came back — the index has no chunk matching this question"
    );
  }

  if (cited.length && console.table) {
    console.log("of those, the answer cited:");
    console.table(cited, ["n", "pdf", "title", "url"]);
  } else if (!cited.length) {
    console.log("cited: none — the model used no citation annotations");
  }

  if (console.groupEnd) console.groupEnd();
}

function App({ restConfig }) {
  const welcomeMessage =
    (restConfig && restConfig.welcomeMessage) ||
    "Greetings! Can I answer any questions for you?";

  const welcomeResetMs =
    (restConfig && typeof restConfig.welcomeResetHours === "number"
      ? restConfig.welcomeResetHours
      : 24) *
    60 *
    60 *
    1000;

  const disclaimer = (restConfig && restConfig.disclaimer) || "";

  let defaultChatDialog = [
    {
      from: "chatbot",
      text:
        (restConfig && restConfig.introMessage) ||
        "Greetings - I’m an AI Chatbot that answers questions based on our published research & insights.",
    },
  ];
  // Second bubble: a flat disclaimer note (not styled as a message).
  if (disclaimer) {
    defaultChatDialog.push({ from: "disclaimer", text: disclaimer });
  }

  document.querySelectorAll(".sigil-bot-button a").forEach((element) => {
    element.onclick = function (e) {
      e.preventDefault();

      let prompt = this.getAttribute("href");
      prompt = prompt.substring(1);

      if (prompt) {
        askQuestion(prompt);
      } else {
        setChatDialogIsActive(true);
      }
    };
  });

  let defaultPreloadedQuestions =
    restConfig && Array.isArray(restConfig.initialQuestions) && restConfig.initialQuestions.length
      ? restConfig.initialQuestions
      : [
          "What does the research say about emergency savings?",
          "Tell me about inclusive investing.",
        ];

  const [chatDialog, setChatDialog] = useState(defaultChatDialog);
  const [preloadedQuestionsArr, setPreloadedQuestionsArr] = useState(
    defaultPreloadedQuestions,
  );
  const [isThinking, setIsThinking] = useState(false);
  const [chatDialogIsActive, setChatDialogIsActive] = useState(false);

  const handleMinimizeAll = () => {
    markWelcomeSeen();
    setChatDialogIsActive(false);
  };

  const handleOffClick = (e) => {
    e.stopPropagation();
    setChatDialogIsActive(false);
  };

  const askQuestion = async (prompt, options = {}) => {
    const text = typeof prompt === "string" ? prompt.trim() : "";
    // NOTE: the old `preloadedQuestionsArr.length <= 0` clause is gone. It was
    // never a valid gate: nothing refills that array after a failure, so the
    // widget went permanently dead, and it also swallowed typed questions for
    // the seconds the follow-up suggestions were being generated.
    if (!text || isThinking) return;

    const isRetry = !!options.retry;

    // Emptying the prompts is what drives the spinner under the chat while we
    // wait, so snapshot them — a failure has to put something back.
    const previousQuestions = preloadedQuestionsArr.length
      ? preloadedQuestionsArr
      : defaultPreloadedQuestions;

    setIsThinking(true);
    setPreloadedQuestionsArr([]);

    if (chatDialogIsActive !== true) {
      setChatDialogIsActive(true);
    }

    // A retry re-uses the question already on screen; a new question appends
    // it. Either way the old error notice goes: it described the attempt being
    // replaced, and it is a status message rather than conversation history.
    setChatDialog((prev) => {
      const kept = prev.filter((m) => m.from !== "error");
      return isRetry ? kept : [...kept, { from: "user", text: text }];
    });

    let resp;
    try {
      resp = await sendPromptToApi(text, restConfig);
    } catch (error) {
      const err = describeError(error);
      setIsThinking(false);
      setPreloadedQuestionsArr(previousQuestions);
      reportChatError(restConfig, err, "chat");
      setChatDialog((prev) => [
        ...prev,
        {
          from: "error",
          text: visitorMessage(err),
          code: errorCodeLabel(err),
          canRetry: isRetryable(err),
          prompt: text,
        },
      ]);
      return;
    }
    setIsThinking(false);
    logDebug(text, resp);

    setChatDialog((prev) => [
      ...prev,
      {
        from: "chatbot",
        text: "<pre>" + (resp.answer || "") + "</pre>",
        citations: resp.citations,
        links: resp.links,
      },
    ]);

    // Follow-up suggestions are internal and best-effort: a failure never
    // raises an error notice, because the visitor didn't ask for them. But it
    // must not leave the question panel spinning forever, and it must not
    // vanish without trace.
    let suggested = null;
    try {
      suggested = await sendPromptToApi(
        "Based on Commonwealth's research and the previous question `" +
          text +
          "`, suggest 2 short follow-up questions (each under 40 characters). Reply with the questions only, separated by '|||'.",
        restConfig,
        true, // internal — not logged to chat history
      );
    } catch (error) {
      reportChatError(restConfig, describeError(error), "suggestions");
    }

    const next = parseSuggestions(suggested && suggested.answer);
    setPreloadedQuestionsArr(next.length ? next : previousQuestions);
  };

  useEffect(() => {
    if (shouldShowWelcome(welcomeResetMs)) {
      setTimeout(() => {
        if (chatDialogIsActive === false) {
          setChatDialogIsActive("half-minimized");
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="Sigil-Bot-App-Wrapper" onClick={handleOffClick}>
      <div
        className="sigil-content-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <ChatDialog
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          askQuestion={askQuestion}
          chatDialog={chatDialog}
        />
        <PreloadedQuestions
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          minimizeAll={handleMinimizeAll}
          askQuestion={askQuestion}
          questions={preloadedQuestionsArr}
          welcome={welcomeMessage}
        />
        <PersonaButton
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          iconUrl={restConfig && restConfig.buttonIconUrl}
          onClick={() =>
            setChatDialogIsActive(
              handleChatDialogActiveState(chatDialogIsActive),
            )
          }
        />
      </div>
    </div>
  );
}

function handleChatDialogActiveState(chatDialogIsActive) {
  markWelcomeSeen();

  let returnVal;
  if (chatDialogIsActive === "half-minimized") {
    returnVal = true;
  } else {
    returnVal = !chatDialogIsActive;
  }
  return returnVal;
}

export default App;
